<template>
  <div class="search-view">
    <!-- 导航按钮 -->
    <div class="navigation-buttons">
      <router-link to="/admin" class="nav-btn">返回管理面板</router-link>
      <router-link to="/" class="nav-btn">返回主页</router-link>
    </div>

    <!-- 搜索表单 -->
    <div class="search-form">
      <h2>文章搜索</h2>
      <div class="form-group">
        <label>日期条件</label>
        <select v-model="searchForm.dateOperator">
          <option value=">=">大于等于</option>
          <option value="<=">小于等于</option>
          <option value="=">等于</option>
          <option value="between">时间段</option>
        </select>
        <input type="date" v-model="searchForm.date" v-if="searchForm.dateOperator !== 'between'" />
        <div v-else class="date-range">
          <input type="date" v-model="searchForm.startDate" />
          <span>至</span>
          <input type="date" v-model="searchForm.endDate" />
        </div>
      </div>
      
      <div class="form-group">
        <label>RSS节点</label>
        <div class="rss-nodes-container">
          <label v-for="node in rssNodes" :key="node.id" class="rss-node-item">
            <input 
              type="checkbox" 
              :value="node.id" 
              v-model="searchForm.rssNodeIds"
            >
            <span>{{ node.siteName }}</span>
          </label>
        </div>
      </div>

      <div class="form-group">
        <label>关键词</label>
        <input type="text" v-model="searchForm.keywords" placeholder="输入搜索关键词" />
      </div>

      <div class="form-group">
        <label>分类</label>
        <input type="text" v-model="searchForm.categories" placeholder="输入分类，多个分类用逗号分隔" />
      </div>

      <div class="form-actions">
        <button @click="search" :disabled="isLoading">搜索</button>
        <button @click="resetForm">重置</button>
      </div>
    </div>

    <!-- 搜索结果 -->
    <div class="search-results" v-if="searchResults">
      <h3>搜索结果 (共 {{ searchResults.totalItems }} 条)</h3>
      
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>标题</th>
            <th>链接</th>
            <th>发布时间</th>
            <th>RSS节点</th>
            <th>分类</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in searchResults.items" :key="item.id">
            <td>{{ item.title }}</td>
            <td><a :href="item.link" target="_blank">{{ item.link }}</a></td>
            <td>{{ item.pubDate }}</td>
            <td>{{ getNodeName(item.rssNodeId) }}</td>
            <td>{{ item.categories }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 分页控件 -->
      <div class="pagination-controls">
        <button 
          @click="changePage(currentPage - 1)" 
          :disabled="currentPage === 1 || isLoading"
        >
          上一页
        </button>
        <div class="page-numbers">
          <button 
            v-for="page in displayedPages" 
            :key="page"
            @click="changePage(page)"
            :class="{ active: page === currentPage }"
            :disabled="isLoading"
          >
            {{ page }}
          </button>
        </div>
        <button 
          @click="changePage(currentPage + 1)" 
          :disabled="currentPage === totalPages || isLoading"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 加载动画 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script src="./SearchView.js"></script>

<style scoped>
.search-view {
  padding: 24px;
  font-size: 0.875rem;
  line-height: 1.6;
  background-color: var(--primary-bg);
  min-height: 100vh;
}

.navigation-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.nav-btn {
  padding: 8px 16px;
  background-color: var(--accent-color);
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.nav-btn:hover {
  background-color: var(--text-primary);
}

.search-form {
  margin-bottom: 24px;
  padding: 20px;
  background-color: var(--secondary-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-form h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--secondary-bg);
  color: var(--text-primary);
}

.form-group select[multiple] {
  height: 120px;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.form-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background-color: var(--accent-color);
  color: white;
}

.form-actions button:hover {
  background-color: var(--text-primary);
}

.search-results {
  margin-top: 24px;
}

.search-results h3 {
  font-size: 1.1rem;
  margin-bottom: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

th, td {
  border: 1px solid var(--border-color);
  padding: 12px;
  text-align: left;
}

th {
  font-weight: 600;
  background-color: var(--hover-bg);
}

tr:hover {
  background-color: var(--hover-bg);
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.pagination-controls button {
  padding: 6px 12px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: var(--text-primary);
}

.pagination-controls button:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.rss-nodes-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  background-color: var(--secondary-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
}

.rss-node-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 2px 0;
}

.rss-node-item input[type="checkbox"] {
  margin: 0;
  padding: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.rss-node-item span {
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.page-numbers {
  display: flex;
  gap: 8px;
  margin: 0 16px;
}

.page-numbers button {
  min-width: 32px;
  padding: 6px 12px;
  background-color: var(--secondary-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.page-numbers button.active {
  background-color: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.page-numbers button:hover:not(:disabled) {
  background-color: var(--hover-bg);
}

.page-numbers button.active:hover:not(:disabled) {
  background-color: var(--text-primary);
}
</style> 